import SpinIcon from '@/icons/SpinIcon'
import { cn } from '@/lib/utils'
import React from 'react'

type SpinnerVariant = 'default' | 'simple' | 'dots'
type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl'

interface SpinnerProps {
  /** Variant of the spinner */
  variant?: SpinnerVariant
  /** Size of the spinner */
  size?: SpinnerSize | number
  /** Custom className */
  className?: string
  /** Whether to show background overlay (for overlay mode) */
  showBackground?: boolean
  /** Whether to position absolutely (overlay mode) or inline */
  overlay?: boolean
  /** Custom color for simple and dots variants */
  color?: string
}

const sizeMap: Record<SpinnerSize, { width: number; height: number; dotSize: number }> = {
  sm: { width: 16, height: 16, dotSize: 4 },
  md: { width: 32, height: 32, dotSize: 8 },
  lg: { width: 48, height: 48, dotSize: 12 },
  xl: { width: 64, height: 64, dotSize: 16 },
}

function SimpleSpinner({
  size,
  color,
  className,
}: {
  size: number
  color: string
  className?: string
}) {
  return (
    <div
      className={cn('animate-spin rounded-full border-b-2', className)}
      style={{
        width: size,
        height: size,
        borderColor: color,
      }}
    />
  )
}

function DotsSpinner({
  dotSize,
  color,
  className,
}: {
  dotSize: number
  color: string
  className?: string
}) {
  return (
    <div className={cn('flex space-x-1', className)}>
      <div
        style={{ width: dotSize, height: dotSize, backgroundColor: color }}
        className="animate-bounce rounded-full [animation-delay:-0.3s]"
      />
      <div
        style={{ width: dotSize, height: dotSize, backgroundColor: color }}
        className="animate-bounce rounded-full [animation-delay:-0.15s]"
      />
      <div
        style={{ width: dotSize, height: dotSize, backgroundColor: color }}
        className="animate-bounce rounded-full"
      />
    </div>
  )
}

export function Spinner({
  variant = 'default',
  size = 'md',
  className = '',
  showBackground = true,
  overlay = false,
  color = 'currentColor',
}: SpinnerProps) {
  // Handle size
  const dimensions =
    typeof size === 'number'
      ? { width: size, height: size, dotSize: Math.max(4, size / 4) }
      : sizeMap[size]

  // Render the appropriate spinner variant
  let spinnerElement: React.ReactNode

  switch (variant) {
    case 'simple':
      spinnerElement = (
        <SimpleSpinner
          size={dimensions.width}
          color={variant === 'simple' && color === 'currentColor' ? '#1f2937' : color}
          className={!overlay ? className : undefined}
        />
      )
      break
    case 'dots':
      spinnerElement = (
        <DotsSpinner
          dotSize={dimensions.dotSize}
          color={variant === 'dots' && color === 'currentColor' ? '#1f2937' : color}
          className={!overlay ? className : undefined}
        />
      )
      break
    case 'default':
    default:
      spinnerElement = (
        <SpinIcon
          width={dimensions.width}
          height={dimensions.height}
          className={!overlay ? className : undefined}
        />
      )
      break
  }

  // For overlay mode
  if (overlay) {
    return (
      <div
        className={cn(
          'absolute left-0 top-0 z-[200] flex h-full w-full items-center justify-center',
          showBackground ? 'bg-black/50 backdrop-blur-sm' : '',
          className
        )}
      >
        {spinnerElement}
      </div>
    )
  }

  // For inline mode
  return (
    <div className={cn('flex items-center justify-center', !className.includes('h-') && 'h-full')}>
      {spinnerElement}
    </div>
  )
}

// Export legacy component names for backward compatibility
export default Spinner
